import React, {useContext, useEffect, useRef, useState} from 'react';
import {Container} from "react-bootstrap";
import PageHeader from "components/PageHeader";
import {AgGridReact} from "ag-grid-react";
import {DEFAULT_GRID_PAGINATION_SIZE, GRID_COLUMN_PROPERTY} from "utils/Constants";
import {dateToString, stringToDate} from "utils/FormatUtils";
import useWindowDimensions from "hooks/Dimensions";
import {ClearingSystemContext} from "./context";
import {SecurityContext} from "context/security";
import {ToastContext} from "context/Toast";
import {ActionPanelContext} from "context/action";
import {addPropertiesToGridColumns} from "utils/gridColumnUtils";
import * as URL from "service/UrlConstant";
import ClearingSystemEditModal from "./ClearingSystemEditModal";
import ActionModal from "components/ActionModal";
import DeleteModal from "components/AgGridToolbar/DeleteModal";

export default function ClearingSystemResultPanel() {

    const {
        clearingSystemResultsDisplayState, setClearingSystemResultsDisplayState,
        clearingSystemFilterState, setShowGridToolbar,
        showEdit, setShowEdit, selectedRecord, setSelectedRecord
    } = useContext(ClearingSystemContext);
    const {makePostRequest} = useContext(SecurityContext);
    const {error, success} = useContext(ToastContext);
    const {setHandleDeleteFunction} = useContext(ActionPanelContext);

    const gridRef = useRef();
    const [rowData, setRowData] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const {isMobile} = useWindowDimensions();

    const defaultColDef = {
        sortable: true,
        resizable: true,
        floatingFilter: true,
        filter: true,
        enableCellChangeFlash: true,
        lockPinned: !!isMobile,
    };

    useEffect(() => {
        if (clearingSystemResultsDisplayState.resData && clearingSystemResultsDisplayState.resData.length > 0)
            setRowData(clearingSystemResultsDisplayState.resData);
        else
            getClearingSystems();
    }, [])

    useEffect(() => {
        setRowData(clearingSystemResultsDisplayState.resData);
    }, [clearingSystemResultsDisplayState])

    const getClearingSystems = () => {
        const params = {
            clearingSystemCode: null,
            clearingSystemId: null,
            agentName: null
        }

        const onSuccessResponse = (response) => {
            setShowGridToolbar(response.data.length >= 0);
            const displayState = JSON.parse(JSON.stringify(clearingSystemResultsDisplayState));
            displayState.resData = ([...response.data]);
            setClearingSystemResultsDisplayState(displayState);
        }
        const onErrorResponse = (err) => {
            console.error(err);
            error("Error getting clearing system data");
        }

        makePostRequest(params, URL.CLEARING_SYSTEM_SEARCH_URL, onSuccessResponse, onErrorResponse);
    };

    const handleRefresh = () => {
        const params = {
            clearingSystemCode: clearingSystemFilterState.clearingSystemCode || null,
            clearingSystemId: clearingSystemFilterState.clearingSystemId || null,
            agentName: clearingSystemFilterState.agentName || null
        }

        const onSuccessResponse = (response) => {
            setShowGridToolbar(response.data.length >= 0);
            const displayState = JSON.parse(JSON.stringify(clearingSystemResultsDisplayState));
            displayState.resData = ([...response.data]);
            displayState.search = params;
            setClearingSystemResultsDisplayState(displayState);
        }
        const onErrorResponse = (err) => {
            console.error(err);
            error("Error refreshing clearing system data");
        }

        makePostRequest(params, URL.CLEARING_SYSTEM_SEARCH_URL, onSuccessResponse, onErrorResponse);
    }

    const handleEditActionClicked = (record) => {
        setSelectedRecord(record);
        setShowEdit(true);
    }

    const handleDeleteActionClicked = (record) => {
        if (!record || !record.id)
            return;

        // Register the delete function so the shared DeleteModal confirmation
        // can trigger it (same logic used by the wire template result page).
        setHandleDeleteFunction({
            handleDelete: (gridApi, onSuccess, onError) => performDelete(record, onSuccess, onError)
        });
        setShowDeleteModal(true);
    }

    const performDelete = (record, onSuccess, onError) => {
        const onSuccessResponse = () => {
            if (onSuccess) onSuccess();
            if (success) success("Clearing system deleted successfully");
            handleRefresh();
        }
        const onErrorResponse = (err) => {
            console.error(err);
            if (onError) onError(err);
            error("Error deleting clearing system");
        }

        makePostRequest({id: record.id}, URL.CLEARING_SYSTEM_DELETE_URL, onSuccessResponse, onErrorResponse);
    }

    const editRenderer = (props) => {
        return (
            <>
                <a className="td-link" href="#" onClick={(e) => {
                    e.preventDefault();
                    handleEditActionClicked(props.data);
                }}>Edit</a>
                <a className="td-link ml-3" href="#" onClick={(e) => {
                    e.preventDefault();
                    handleDeleteActionClicked(props.data);
                }}>Delete</a>
            </>
        );
    }

    const onGridReady = (params) => {
        params.api.sizeColumnsToFit();
    }

    const onDataRendered = (params) => {
        params.columnApi.autoSizeAllColumns();
    }

    const updatedColumnDefs = addPropertiesToGridColumns(columnDefsResultsPanel, GRID_COLUMN_PROPERTY);

    return (<>
        <Container fluid>
            <PageHeader text={"Clearing System"}/>
            <div className="ag-theme-alpine">
                <AgGridReact
                    ref={gridRef}
                    domLayout={'autoHeight'}
                    frameworkComponents={{
                        editRenderer: editRenderer
                    }}
                    paginationPageSize={DEFAULT_GRID_PAGINATION_SIZE}
                    suppressDragLeaveHidesColumns={true}
                    suppressScrollOnNewData={true}
                    enableSorting={true}
                    enableFilter={true}
                    pagination={true}
                    onGridReady={onGridReady}
                    onFirstDataRendered={onDataRendered}
                    rowDataUpdated={onDataRendered}
                    defaultColDef={defaultColDef}
                    columnDefs={updatedColumnDefs}
                    rowData={rowData}
                    animateRows={true}
                    popupParent={document.body}
                >
                </AgGridReact>
            </div>
        </Container>
        {showEdit &&
        <ClearingSystemEditModal
            show={showEdit}
            setShow={setShowEdit}
            selectedRecord={selectedRecord}
            handleRefresh={handleRefresh}
        />}
        <ActionModal
            show={showDeleteModal}
            onHide={() => setShowDeleteModal(false)}
            title={() => 'Delete'}
            centered={true}
            body={() => (
                <DeleteModal setShow={setShowDeleteModal} setShowDelete={setShowDeleteModal}
                             confirmDeleteBtnClassName="btn btn-block td-btn-primary-light"/>
            )}
        />
    </>);
}

export const columnDefsResultsPanel = [
    {
        headerName: 'Actions', field: 'actions', colId: 'actions',
        cellRenderer: 'editRenderer', suppressMovable: true, lockPosition: true,
        filter: false, sortable: false, maxWidth: 160, suppressColumnsToolPanel: true
    },
    {headerName: 'id', field: 'id', hide: true, colId: 'id', suppressColumnsToolPanel: true},
    {headerName: 'Clearing System Code', field: 'clearingSystemCode'},
    {headerName: 'Clearing System ID', field: 'clearingSystemId'},
    {headerName: 'Agent Name', field: 'agentName'},
    {headerName: 'Agent City', field: 'agentCity'},
    {headerName: 'Agent Province', field: 'agentProvince'},
    {headerName: 'Agent Country', field: 'agentCountry'},
    {headerName: 'Created By', field: 'createdBy'},
    {
        headerName: 'Created At', field: 'createdAt',
        valueGetter: (params) => params.data.createdAt ? stringToDate(dateToString(params.data.createdAt)) : ''
    },
    {headerName: 'Updated By', field: 'updatedBy'},
    {
        headerName: 'Updated At', field: 'updatedAt',
        valueGetter: (params) => params.data.updatedAt ? stringToDate(dateToString(params.data.updatedAt)) : ''
    },
];

